/* eslint-disable no-nested-ternary */
import React, { useEffect } from "react";
import { bindActionCreators, compose } from "redux";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import moment from "moment";
import _ from "lodash";
import numeral from "numeral";
import { ModuleAccordionGroup, ModuleAccordionSummary, PrimaryButton, ButtonSizeOptions } from "@loanbossinc/component-library";

import { PROPERTY_SECTION_KEYS } from "../property.constants";

import RentRollsDrawer from "./property-rent-rolls-drawer";
import { actionCreators as rentRollsActionCreators } from "./rentRolls.store";
import { SuitesRowContainer, SuitesRow, SuiteNumber, SquareFootage, RowText } from "./styles";

const getSuitesRows = (allSuites, handleEditSuite) => {
  const suitesWithNoExpiredLeases = allSuites.map(suite => {
    const notExpiredLeases = suite.leases.filter(lease => moment(lease.leaseEndDate).isAfter(moment()));
    const sortedLeases = _.orderBy(notExpiredLeases, [lease => lease.leaseStartDate], ["asc"]);

    return { ...suite, leases: sortedLeases };
  });

  const sortedSuites = _.orderBy(suitesWithNoExpiredLeases, [suite => `${suite.suiteNumber}`], ["asc"]);

  const defineRowBorder = (suitesLength, leasesLength, suiteIdx, leaseIdx) => {
    if (suiteIdx === suitesLength - 1 && leaseIdx === leasesLength - 1) {
      return "none";
    } else if (leaseIdx === leasesLength - 1) {
      return "1px solid #dadce0";
    }

    return "1px dashed #dadce0";
  };

  return (
    <>
      <SuitesRowContainer header border="1px solid #dadce0" className="suites-row">
        <SuitesRow w="15%" align="left">
          SUITE
        </SuitesRow>
        <SuitesRow w="15%" align="left">
          SQ. FT.
        </SuitesRow>
        <SuitesRow w="32%" align="left">
          LESSEE
        </SuitesRow>
        <SuitesRow w="23%" align="center">
          LEASE DATES
        </SuitesRow>
        <SuitesRow w="15%" align="center">
          BASE RENT (CURRENT)
        </SuitesRow>
      </SuitesRowContainer>
      {sortedSuites.map((suite, suiteIdx) => {
        const { leases } = suite;

        if (!leases.length) {
          return (
            <SuitesRowContainer border={defineRowBorder(sortedSuites.length, leases.length, suiteIdx, -1)}>
              <SuitesRow w="15%" align="left">
                <SuiteNumber>
                  <button onClick={() => handleEditSuite(suite.id)}>{suite.suiteNumber ? suite.suiteNumber : "--"}</button>
                </SuiteNumber>
              </SuitesRow>
              <SuitesRow w="15%" align="left">
                <SquareFootage>{suite.squareFootage ? numeral(suite.squareFootage).format("0,0") : "--"}</SquareFootage>
              </SuitesRow>
            </SuitesRowContainer>
          );
        }

        return leases.map((lease, idx) => {
          const leaseEndDate = lease.leaseEndDate ? moment.utc(lease.leaseEndDate).format("MM/D/YY") : "--";
          const leaseStartDate = lease.leaseStartDate ? moment.utc(lease.leaseStartDate).format("MM/D/YY") : "--";

          const filteredRents = lease.scheduledRents.filter(rent => moment.utc() >= moment.utc(rent.startDate) && moment.utc() <= moment.utc(rent.endDate) && rent.chargeType === "Rent");

          let baseRent = "--";

          if (filteredRents && filteredRents.length) {
            const totalRent = filteredRents.reduce((acc, rent) => {
              return acc + rent.monthlyAmount;
            }, 0);
            baseRent = numeral(totalRent).format("$0,0");
          }

          return (
            <SuitesRowContainer key={lease.id} border={defineRowBorder(sortedSuites.length, leases.length, suiteIdx, idx)}>
              <SuitesRow w="15%" align="left">
                {idx === 0 ? (
                  <SuiteNumber>
                    <button onClick={() => handleEditSuite(suite.id)}>{suite.suiteNumber ? suite.suiteNumber : "--"}</button>
                  </SuiteNumber>
                ) : (
                  ""
                )}
              </SuitesRow>
              <SuitesRow w="15%" align="left">
                {idx === 0 ? <SquareFootage>{suite.squareFootage ? numeral(suite.squareFootage).format("0,0") : "--"}</SquareFootage> : ""}
              </SuitesRow>
              <SuitesRow w="32%" align="left">
                <RowText>{lease.lesseeName ? lease.lesseeName : "--"}</RowText>
              </SuitesRow>
              <SuitesRow w="23%" align="center">
                <RowText>{`${leaseStartDate} - ${leaseEndDate}`}</RowText>
              </SuitesRow>
              <SuitesRow w="15%" align="center">
                <RowText>{baseRent}</RowText>
              </SuitesRow>
            </SuitesRowContainer>
          );
        });
      })}
    </>
  );
};

const PropertyRentRolls = props => {
  const {
    openModal,
    closeModal,
    propertyId,
    companyId,
    allSuites,
    isEditingSuite,
    selectedSuite,
    getRentRolls,
    setRentRollsSelections,
    setRentRollsToDefault,
    shouldRefetchRentRolls,
    ...other
  } = props;

  const [openDrawer, setOpenDrawer] = React.useState(false);

  useEffect(() => {
    if (!allSuites) {
      getRentRolls({ propertyId, dealId: props.match?.params?.dealId });
    }
  }, [propertyId, allSuites]);

  useEffect(
    () => () => {
      setRentRollsToDefault();
    },
    [setRentRollsToDefault]
  );

  useEffect(() => {
    if (shouldRefetchRentRolls) {
      getRentRolls({ propertyId, dealId: props.match?.params?.dealId });
    }
  }, [shouldRefetchRentRolls, propertyId, getRentRolls]);

  const closeDrawer = () => setOpenDrawer(false);

  const handleEditSuite = suiteId => {
    const selectedSuiteToEdit = allSuites.find(suite => suite.id === suiteId);
    setRentRollsSelections({ isEditingSuite: true, activeSuiteId: suiteId, selectedSuite: selectedSuiteToEdit });
    setOpenDrawer(true);
  };

  return (
    <>
      <ModuleAccordionGroup
        {...other}
        groups={[
          {
            title: "Rent Rolls",
            key: PROPERTY_SECTION_KEYS.PROPERTY_RENT_ROLLS,
            isButton: false,
            isExpandable: true,
            isTitleGroup: true,
            buttonLabel: "Upload a rent roll",
            showEditButton: true,
            onClick: () => {
              openModal("Upload a rent roll", () => {
                closeModal();
              });
            },
            textOnlyButton: true,
            sections: [
              allSuites?.length ? getSuitesRows(allSuites, handleEditSuite) : null,
              <ModuleAccordionSummary
                isExpandable={false}
                title={<PrimaryButton label="Add suite" onClick={() => setOpenDrawer(true)} size={ButtonSizeOptions.sm} />}
              />
            ]
          }
        ]}
      />
      <RentRollsDrawer
        closeDrawer={closeDrawer}
        isOpen={openDrawer}
        setOpenDrawer={setOpenDrawer}
        dealId={props.match?.params?.dealId}
        drawerMinWidth={700}
        companyId={companyId}
        {...props}
      />
    </>
  );
};

const mapStateToProps = ({ rentRolls, uploadRentRollModal }) => ({ ...rentRolls, ...uploadRentRollModal });

const mapDispatchToProps = dispatch => bindActionCreators({ ...rentRollsActionCreators }, dispatch);

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(PropertyRentRolls);
